import { useState } from "react";
import { Button } from "@/components/ui/button";
import { LogOut, Monitor, ShoppingBag, IdCard, ArrowRight, User, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import InstallGuideDialog from "@/components/InstallGuideDialog";
import ProfileDialog from "@/components/ProfileDialog";
import ShareAppButton from "@/components/ShareAppButton";
import LockedScreen from "@/pages/LockedScreen";
import { AdBanner } from "@/components/AdBanner";

const FEATURES = [
  {
    id: "orders",
    title: "Order Management",
    hindi: "\u0911\u0930\u094D\u0921\u0930 \u092E\u0948\u0928\u0947\u091C\u092E\u0947\u0902\u091F",
    desc: "Manage daily customer orders, products, PDF invoices and date-wise reports.",
    icon: ShoppingBag,
    active: true,
    color: "bg-cyan-50 text-cyan-700 border-cyan-200",
    iconBg: "bg-cyan-600",
  },
  {
    id: "id-certificate",
    title: "ID / Certificate Creator",
    hindi: "\u0906\u0908\u0921\u0940 / \u0938\u0930\u094D\u091F\u093F\u092B\u093F\u0915\u0947\u091F",
    desc: "Create fields or upload Excel, design templates, and generate bulk PDF/JPG IDs & certificates.",
    icon: IdCard,
    active: true,
    color: "bg-pink-50 text-pink-700 border-pink-200",
    iconBg: "bg-pink-600",
  },
  {
    id: "events",
    title: "Create Event",
    hindi: "\u0907\u0935\u0947\u0902\u091F",
    desc: "Organise events with custom submission forms, public link/QR, approvals, payments and Excel/PDF exports.",
    icon: CalendarPlus,
    active: true,
    requiresPlan: true,
    color: "bg-amber-50 text-amber-700 border-amber-200",
    iconBg: "bg-amber-600",
  },
];

export default function FeaturesPage({ shop, onLogout, onInstall, canInstall, onSelect, onShopUpdate }) {
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showPlan, setShowPlan] = useState(false);

  const planEligible = !shop.valid_until || new Date(shop.valid_until) > new Date();
  const disabledFeatures = shop.disabled_features || [];

  const handleClick = (f) => {
    if (disabledFeatures.includes(f.id)) {
      setShowPlan(true);
      return;
    }
    if (f.requiresPlan && !planEligible) {
      toast.info("Ye feature plan lene ke baad milega", { description: "Trial ke baad plan activate hone par Create Event use kar payenge." });
      return;
    }
    if (f.active) {
      onSelect(f.id);
    } else {
      toast.info("This feature is coming soon!", { description: "This feature is under development." });
    }
  };

  if (showPlan) {
    return (
      <LockedScreen
        shop={shop}
        onLogout={onLogout}
        onClose={() => setShowPlan(false)}
        title="Activate Your Plan"
        message="Ye feature abhi band hai. Plan lekar ise turant use karein — niche diye QR/number se payment karein ya Admin ko call karein. Payment ke baad screenshot bhejne par aapka plan activate ho jayega."
      />
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="features-page">
      <header className="sticky top-0 z-20 bg-white border-b border-border">
        <div className="h-1 flex">
          <div className="flex-1 bg-cyan-500" />
          <div className="flex-1 bg-pink-600" />
          <div className="flex-1 bg-yellow-400" />
          <div className="flex-1 bg-slate-900" />
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2 sm:gap-3">
          <img src="/logo.webp?v=2" alt="Sootrik" className="h-8 sm:h-9 w-auto" data-testid="header-logo" />
          <div className="w-full sm:w-auto sm:flex-1 sm:min-w-0 order-2 sm:order-none">
            <p className="font-heading font-bold text-sm sm:text-base truncate" data-testid="header-shop-name">{shop.name}</p>
            <p className="text-xs text-muted-foreground truncate">Mob: {shop.mobile}</p>
          </div>
          <div className="flex items-center gap-1 sm:gap-1.5 shrink-0 ml-auto">
          <ShareAppButton className="gap-1.5 border-pink-500 text-pink-700 hover:bg-pink-50" testId="features-share-app-btn" />
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (canInstall) onInstall();
              else setShowInstallGuide(true);
            }}
            data-testid="pwa-install-btn"
            className="gap-1.5 border-cyan-500 text-cyan-700 hover:bg-cyan-50"
          >
            <Monitor className="h-4 w-4" />
            <span className="hidden sm:inline">Install App</span>
            <span className="sm:hidden">Install</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowProfile(true)} data-testid="profile-btn" className="gap-1.5 text-muted-foreground hover:text-foreground">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Profile</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={onLogout} data-testid="logout-btn" className="gap-1.5 text-muted-foreground hover:text-foreground">
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
          </div>
        </div>
      </header>

      <AdBanner className="pt-5" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Sootrik</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1" data-testid="features-heading">
          Our Features
        </h1>
        <p className="text-muted-foreground text-sm mt-2">Click Here</p>
        {shop.valid_until && !shop.plan_active && (
          <p className="mt-4 inline-block text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5" data-testid="trial-banner">
            Free Trial : 6 Days
          </p>
        )}

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mt-8 max-w-3xl">
          {FEATURES.map((f) => {
            const isOff = disabledFeatures.includes(f.id);
            return (
            <button
              key={f.id}
              onClick={() => handleClick(f)}
              data-testid={`feature-card-${f.id}`}
              className={`relative text-left bg-card border rounded-xl p-6 shadow-sm transition-all duration-200 ${
                isOff
                  ? "border-red-200 opacity-60 cursor-not-allowed"
                  : f.active
                  ? "border-border hover:shadow-md hover:-translate-y-0.5 hover:border-cyan-400 cursor-pointer"
                  : "border-border opacity-80 cursor-pointer"
              }`}
            >
              {isOff ? (
                <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 px-2 py-1 rounded-full" data-testid={`feature-disabled-badge-${f.id}`}>
                  Inactive
                </span>
              ) : !f.active ? (
                <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full" data-testid={`feature-badge-${f.id}`}>
                  Coming Soon
                </span>
              ) : f.requiresPlan && !planEligible ? (
                <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-1 rounded-full" data-testid={`feature-plan-badge-${f.id}`}>
                  Plan
                </span>
              ) : null}
              <span className={`inline-flex p-3 rounded-xl text-white ${f.iconBg}`}>
                <f.icon className="h-6 w-6" />
              </span>
              <h2 className="font-heading text-lg font-bold mt-4">{f.title}</h2>
              <p className="text-sm text-muted-foreground mt-2">{isOff ? "Admin ne ye feature band kiya hai. Chalu karwane ke liye admin se sampark karein." : f.desc}</p>
              {f.active && !isOff && (
                <span className="inline-flex items-center gap-1 text-cyan-700 text-sm font-semibold mt-4">
                  Open <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </button>
            );
          })}
        </div>
      </main>

      <InstallGuideDialog
        open={showInstallGuide}
        onClose={() => setShowInstallGuide(false)}
        onDirectInstall={onInstall}
        canDirectInstall={canInstall}
      />
      <ProfileDialog
        open={showProfile}
        onClose={() => setShowProfile(false)}
        shop={shop}
        onUpdated={onShopUpdate}
      />
    </div>
  );
}
